/**
 * Minimal Markdown renderer for blog post bodies — no external dependency.
 * Supports exactly what our writers produce: "## "/"### " headings, "- " bullet
 * lists, blank-line-separated paragraphs, **bold**, *italic* and [text](https://…)
 * links inline. Anything else is
 * treated as paragraph text. Intentionally tiny and safe (no raw HTML).
 */
import { Fragment, type ReactNode } from 'react';

function renderInline(text: string): ReactNode[] {
  // Split on [text](https://url) links, then **bold** (checked before) *italic*.
  return text.split(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*)/g).map((part, i) => {
    const a = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/.exec(part);
    if (a) {
      return (
        <a key={i} href={a[2]} target="_blank" rel="noopener noreferrer" className="text-accent-600 underline underline-offset-2 hover:text-accent-700 [overflow-wrap:anywhere]">
          {a[1]}
        </a>
      );
    }
    const b = /^\*\*([^*]+)\*\*$/.exec(part);
    if (b) return <strong key={i} className="font-semibold text-ink-900">{b[1]}</strong>;
    const em = /^\*([^*]+)\*$/.exec(part);
    if (em) return <em key={i} className="italic">{em[1]}</em>;
    return <Fragment key={i}>{part}</Fragment>;
  });
}

export function renderMarkdown(md: string): ReactNode[] {
  const lines = md.replace(/\r/g, '').split('\n');
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: string[] = [];
  let key = 0;

  const flushPara = () => {
    if (para.length) {
      blocks.push(
        <p key={key++} className="my-4 leading-relaxed text-ink-700">{renderInline(para.join(' '))}</p>,
      );
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ul key={key++} className="my-4 space-y-1.5">
          {list.map((it, i) => (
            <li key={i} className="flex gap-2.5 text-ink-700 leading-relaxed">
              <span className="text-accent-500 mt-1 shrink-0" aria-hidden="true">•</span>
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>,
      );
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    if (line.startsWith('### ')) { flushPara(); flushList(); blocks.push(<h3 key={key++} className="font-serif text-lg text-ink-900 mt-6 mb-2">{renderInline(line.slice(4))}</h3>); continue; }
    if (line.startsWith('## ')) { flushPara(); flushList(); blocks.push(<h2 key={key++} className="font-serif text-xl md:text-2xl text-ink-900 mt-8 mb-3 tracking-tight">{renderInline(line.slice(3))}</h2>); continue; }
    if (line.startsWith('- ')) { flushPara(); list.push(line.slice(2)); continue; }
    para.push(line);
  }
  flushPara();
  flushList();
  return blocks;
}
